import { useEffect, useState } from 'react'
import Login from './pages/Login'
import Lobby from './pages/Lobby'
import Room  from './pages/Room'
import Play  from './pages/Play'
import PlayTicket    from './pages/PlayTicket'
import PlayRummikub  from './pages/PlayRummikub'
import ResumeGameModal from './components/ResumeGameModal'
import { AuthState, auth } from './store/auth'
import { roomStore } from './store/room'
import { catanStore } from './store/catan'
import * as hub from './hub/roomHub'
import * as catanHub from './hub/catanHub'
import { apiRoomMy, apiCatanGameState, apiTtrGameState, apiRummikubGameState } from './server/ServiceCall'
import { sound } from './util/sound'
import './css/App.css'

// 페이지 상태머신 — SoT §3-1 라우트 매핑 (react-router 미사용)
//   'play'        → /play/:roomCode         (카탄)
//   'play_ticket' → /play/ticket/:roomCode  (티켓 투 라이드, Phase 0 신규)
type Page = 'login' | 'lobby' | 'room' | 'play' | 'play_ticket' | 'play_rummikub'

/** 자동 복귀 대기 정보 (사용자 모달 응답 전 보존) */
interface PendingResume {
  roomCode:  string
  isStarted: boolean
  gameData:  Record<string, unknown> | null
  matchId:   number
  gameType:  'CATAN' | 'TICKET' | 'RUMK'   // SEQ=10193: 자동 복귀 게임 분기 (apiCatanGameState/apiTtrGameState 선택)
}

function App() {
  const [page,           setPage]           = useState<Page>('login')
  const [authState,      setAuthState]      = useState<AuthState | null>(null)
  const [currentRoom,    setCurrentRoom]    = useState<string>('')
  const [currentMatchId, setCurrentMatchId] = useState<number>(0)
  // 진행 중 게임 복귀 모달 (2026-05-06)
  const [pendingResume,  setPendingResume]  = useState<PendingResume | null>(null)

  // 초기 로드: localStorage 토큰 복원 + 자동 복귀 (Phase 1.5 + Phase 2)
  // 2026-05-06: 자동 진입 폐지 — 진행 중 게임/룸 발견 시 모달 노출 (예/아니오 사용자 선택)
  // SEQ=10110 STEP=4: 효과음 매니저 preload (앱 부팅 시 1회)
  useEffect(() => { sound.preload() }, [])

  useEffect(() => {
    const a = auth.get()
    if (!a) return

    setAuthState(a)

    // 토큰 복원 즉시 SignalR 연결 시작 (preemptive — 자동 복귀로 lobby 건너뛰는 경우 대비)
    hub.startConnection(a.token).catch(e => console.error('SignalR 시작 실패', e))

    apiRoomMy(a.token).then(async result => {
      if (result?.room) {
        roomStore.applyDetail(result as any)
        const r        = result.room as any
        const roomCode = r.ROOM_CODE ?? r.roomCode ?? ''
        const status   = r.STATUS   ?? r.status   ?? ''
        // SEQ=10193: 자동 복귀 대상 게임 종류 분기 (apiRoomMy 응답의 GAME_TYPE 캡처)
        // 기본 CATAN으로 fallback — 기존 동작 보존
        const gameType = (((r.GAME_TYPE ?? r.gameType ?? 'CATAN') as string).toUpperCase()) as 'CATAN' | 'TICKET' | 'RUMK'
        if (roomCode) {
          // STATUS=STARTED면 게임별 매치 데이터 미리 로드 (모달 [예] 응답 시 즉시 Play 진입)
          let gameData: Record<string, unknown> | null = null
          let matchId = 0
          if (status === 'STARTED') {
            if (gameType === 'TICKET') {
              // SEQ=10193: TTR 매치는 PID 21101 apiTtrGameState 사용 (matchHeader.matchId 경로)
              const gd = await apiTtrGameState(a.token, roomCode, 0).catch(() => null)
              if (gd) {
                gameData = gd as Record<string, unknown>
                matchId = (gd as { matchHeader?: { matchId?: number } }).matchHeader?.matchId ?? 0
              }
            } else if (gameType === 'RUMK') {
              // SEQ=10220: 루미큐브 매치는 PID 22001 apiRummikubGameState 사용 (matchHeader.matchId 경로)
              const gd = await apiRummikubGameState(a.token, roomCode, 0).catch(() => null)
              if (gd) {
                gameData = gd as Record<string, unknown>
                matchId = (gd as { matchHeader?: { matchId?: number; match_id?: number } }).matchHeader?.matchId
                  ?? (gd as { matchHeader?: { match_id?: number } }).matchHeader?.match_id
                  ?? 0
              }
            } else {
              // CATAN (기존 동작 보존)
              const gd = await apiCatanGameState(a.token, roomCode, 0).catch(() => null)
              if (gd) {
                gameData = gd as Record<string, unknown>
                matchId = (gd as { matchId?: number }).matchId ?? 0
              }
            }
          }
          // 모달 노출 — lobby 페이지를 배경으로 두고 사용자 선택 대기
          setPendingResume({
            roomCode,
            isStarted: status === 'STARTED' && gameData !== null,
            gameData,
            matchId,
            gameType,
          })
          setPage('lobby')
          return
        }
      }
      setPage('lobby')
    }).catch(() => {
      setPage('lobby')
    })

    // 97 강제 로그아웃 이벤트 처리
    function onSessionExpired() {
      hub.stopConnection().catch(() => {})
      catanHub.stopConnection().catch(() => {})
      roomStore.clearRoom()
      catanStore.clearGame()
      auth.clear()
      setAuthState(null)
      setCurrentRoom('')
      setCurrentMatchId(0)
      setPage('login')
    }
    window.addEventListener('session-expired', onSessionExpired)
    return () => window.removeEventListener('session-expired', onSessionExpired)
  }, [])

  function handleLoggedIn(a: AuthState) {
    setAuthState(a)
    // 로그인 직후 SignalR 연결 시작 (Lobby에서도 시작하지만 race 회피)
    hub.startConnection(a.token).catch(e => console.error('SignalR 시작 실패', e))
    setPage('lobby')
  }

  function handleLogout() {
    hub.stopConnection().catch(() => {})
    catanHub.stopConnection().catch(() => {})
    roomStore.clearRoom()
    catanStore.clearGame()
    auth.clear()
    setAuthState(null)
    setCurrentRoom('')
    setCurrentMatchId(0)
    setPage('login')
  }

  function handleEnterRoom(roomCode: string) {
    setCurrentRoom(roomCode)
    setPage('room')
  }

  function handleLeaveRoom() {
    roomStore.clearRoom()
    catanStore.clearGame()
    setCurrentRoom('')
    setCurrentMatchId(0)
    setPage('lobby')
  }

  // SEQ=10110 STEP=4 — MyInfoModal 닉네임 변경 성공 시 AuthState 갱신
  function handleNicknameChanged(newNickname: string) {
    setAuthState(prev => (prev ? { ...prev, nickname: newNickname } : prev))
  }

  // ── 진행 중 게임 복귀 모달 핸들러 (2026-05-06) ───────────
  function handleResumeYes() {
    if (!pendingResume) return
    const p = pendingResume
    setPendingResume(null)
    setCurrentRoom(p.roomCode)
    if (p.isStarted && p.gameData) {
      setCurrentMatchId(p.matchId)
      if (p.gameType === 'TICKET') {
        // SEQ=10193: TTR 자동 복귀 — PlayTicket mount 시 apiTtrGameState 재호출로 hydrate
        //   App.tsx 단계에서는 setPage만 호출. ticketStore hydration은 PlayTicket useEffect 책임.
        setPage('play_ticket')
      } else if (p.gameType === 'RUMK') {
        // SEQ=10214: 루미큐브 자동 복귀 — PlayRummikub mount 시 reconnectGame invoke로 hydrate
        setPage('play_rummikub')
      } else {
        // CATAN (기존 동작 보존)
        catanStore.applyGameState(p.gameData)
        setPage('play')
      }
    } else {
      setPage('room')
    }
  }
  function handleResumeNo() {
    // RoomLeave invoke는 ResumeGameModal 내부에서 처리됨. 여기는 클라 측 정리 + lobby 이동.
    roomStore.clearRoom()
    catanStore.clearGame()
    setPendingResume(null)
    setCurrentRoom('')
    setCurrentMatchId(0)
    setPage('lobby')
  }

  // Room.tsx의 onGameStarting payload에서 matchId 추출 (카탄)
  function handleGameStart(roomCode: string, matchId?: number) {
    setCurrentRoom(roomCode)
    setCurrentMatchId(matchId ?? 0)
    setPage('play')
  }

  // SEQ=10147 — TTR 게임 시작 (RoomHub.StartGame broadcast gameType=TICKET 수신)
  function handleGameStartTicket(roomCode: string, matchId?: number) {
    setCurrentRoom(roomCode)
    setCurrentMatchId(matchId ?? 0)
    setPage('play_ticket')
  }

  // SEQ=10214 — 루미큐브 게임 시작 (RoomHub.StartGame broadcast gameType=RUMK 수신)
  function handleGameStartRummikub(roomCode: string, matchId?: number) {
    setCurrentRoom(roomCode)
    setCurrentMatchId(matchId ?? 0)
    setPage('play_rummikub')
  }

  // "새 게임" — Play.tsx가 RoomRestarted 수신 시 호출 (SEQ=10076 흐름 변경)
  // 방이 WAITING 으로 전환됐으므로 게임 화면이 아니라 대기실(Room)로 이동.
  // 이전 게임 store/CatanHub 연결을 정리 — 이후 StartGame 시점에 새로 연결된다.
  async function handleRoomRestarted(roomCode: string) {
    catanStore.clearGame()
    await catanHub.stopConnection().catch(() => {})
    setCurrentRoom(roomCode)
    setCurrentMatchId(0)
    setPage('room')
  }

  return (
    <div className="app">
      {page === 'login' || !authState ? (
        <Login onLoggedIn={handleLoggedIn} />
      ) : page === 'play' && currentRoom ? (
        <Play
          key={currentMatchId}
          authState={authState}
          roomCode={currentRoom}
          matchId={currentMatchId || undefined}
          onLeave={handleLeaveRoom}
          onLogout={handleLogout}
          onRoomRestarted={handleRoomRestarted}
          onNicknameChanged={handleNicknameChanged}
        />
      ) : page === 'play_ticket' && currentRoom ? (
        // TTR Phase 0 — Room/Lobby 의 게임 시작 분기는 Phase 1+ (TtrGameStarted 이벤트 수신 시 setPage('play_ticket'))
        <PlayTicket
          key={currentMatchId}
          authState={authState}
          roomCode={currentRoom}
          matchId={currentMatchId || undefined}
          onLeave={handleLeaveRoom}
          onLogout={handleLogout}
        />
      ) : page === 'play_rummikub' && currentRoom ? (
        // SEQ=10214: 루미큐브 Phase 0 — /play/rummikub/:roomCode (TD §3.1 정합)
        <PlayRummikub
          key={currentMatchId}
          authState={authState}
          roomCode={currentRoom}
          matchId={currentMatchId || undefined}
          onLeave={handleLeaveRoom}
          onLogout={handleLogout}
        />
      ) : page === 'room' && currentRoom ? (
        <Room
          authState={authState}
          roomCode={currentRoom}
          onLeave={handleLeaveRoom}
          onLogout={handleLogout}
          onGameStart={handleGameStart}
          onGameStartTicket={handleGameStartTicket}
          onGameStartRummikub={handleGameStartRummikub}
          onNicknameChanged={handleNicknameChanged}
        />
      ) : (
        <Lobby
          authState={authState}
          onLogout={handleLogout}
          onEnterRoom={handleEnterRoom}
          onNicknameChanged={handleNicknameChanged}
        />
      )}
      {/* 진행 중 게임 복귀 모달 — 자동 진입 폐지, 사용자 선택 (2026-05-06) */}
      {pendingResume && authState && (
        <ResumeGameModal
          roomCode={pendingResume.roomCode}
          isStarted={pendingResume.isStarted}
          onResume={handleResumeYes}
          onForfeit={handleResumeNo}
        />
      )}
    </div>
  )
}

export default App
